import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostJson } from './client'
import type { RustdeskDeviceState } from './settingsRemoteConnect'

/**
 * Lansarea unei conexiuni RustDesk - perechea lui
 * `Remote_connect::rustdesk_launch($deviceId, $ticketId)`:
 *
 *   GET /remote-connect/rustdesk-launch/{deviceId}?ticket={ticketId}
 *
 * De pe 12 octombrie 2026 (CEC 64) pagina NU mai primeste nicio parola
 * permanenta - nici a statiei, nici a conturilor de administrare. Intrarea se
 * cere separat, cu o parola de unica folosinta:
 *
 *   POST /remote-connect/rustdesk-launch/{deviceId}/start      {ticket, stealth}
 *   GET  /remote-connect/rustdesk-launch/{deviceId}/start/{id} starea; parola O DATA
 *
 * Regulile sunt toate pe server, in `RemoteConnect\Rustdesk\RustdeskLaunch`.
 */

export type RustdeskLaunchIdentity = {
  rustdeskId: string
  hostname: string
  ip: string
  lastReportedAt: string
}

export type RustdeskLaunchAccount = {
  label: string
  username: string
  /** Seiful in care sta parola (`local`, `hashicorp`). Parola nu vine niciodata. */
  vault: string
}

export type RustdeskLaunchKit = {
  label: string
  icon: string
  url: string
  hint: string
}

export type RustdeskLaunchPage = {
  pageTitle: string
  device: { id: number; name: string }
  ticket: number | null
  /** "Enable remote access" stinsa in Remote Permissions: nici ID, nici parola. */
  accessDisabled: boolean
  /** Statia cere acceptul omului (approve-mode = click): parola nu vine. */
  askToAccept: boolean
  /** `null` = agentul n-a raportat inca, sau identitatea e a altui furnizor. */
  identity: RustdeskLaunchIdentity | null
  adminAccounts: RustdeskLaunchAccount[]
  oneTimePassword: { ttlSeconds: number }
  /** Stealth: permis numai daca firma il permite SI rolul il are. */
  stealth: { allowed: boolean; companyAllows: boolean; roleHas: boolean }
  kits: RustdeskLaunchKit[]
  /**
   * `otp` = parola de unica folosinta. `permanent` = plasa pentru o statie cu
   * agent vechi, cat timp firma tine parola permanenta: `password` e parola
   * statiei, ca inainte de 12 octombrie 2026 (null = neraportata).
   */
  passwordMode?: 'otp' | 'permanent'
  password?: string | null
}

/**
 * Starea unei lansari. `password` vine o singura data, in starea `armed`;
 * dupa aceea starea e `revealed`, fara parola.
 */
export type RustdeskLaunchState = {
  launch: number
  state: 'pending' | 'claimed' | 'armed' | 'revealed' | 'ready' | 'expired' | 'failed' | 'closed'
  mode?: 'password' | 'click'
  stealth?: boolean
  expiresAt?: string | null
  password?: string
  message?: string
}

export function startRustdeskLaunch(deviceId: number, ticketId: number, stealth: boolean) {
  return apiPostJson<RustdeskLaunchState>(`/remote-connect/rustdesk-launch/${deviceId}/start`, {
    ticket: ticketId,
    stealth,
  })
}

export function rustdeskLaunchState(deviceId: number, launchId: number) {
  return apiGet<RustdeskLaunchState>(`/remote-connect/rustdesk-launch/${deviceId}/start/${launchId}`, {
    cache: 'no-store',
  })
}

export function rustdeskLaunchQuery(deviceId: number, ticketId: number) {
  return queryOptions({
    queryKey: ['remote-connect', 'rustdesk-launch', deviceId, ticketId],
    queryFn: () =>
      apiGet<RustdeskLaunchPage>(
        `/remote-connect/rustdesk-launch/${deviceId}${ticketId > 0 ? `?ticket=${ticketId}` : ''}`,
      ),
    /* Parole: nu se tin in cache-ul de intrebari mai mult decat cat e pagina
       deschisa, si nu se recer singure la fiecare focus. */
    staleTime: Infinity,
    gcTime: 0,
  })
}

/** Adresa paginii de lansare, exact ca in portal (`hostUrlJoin`), sub /app. */
export function rustdeskLaunchUrl(deviceId: number, ticketId: number): string {
  return `/app/remote_connect/rustdesk_launch/${deviceId}${ticketId > 0 ? `/${ticketId}` : ''}`
}

/**
 * Instalarea RustDesk la cerere pe un aparat (`RemoteConnect\Rustdesk\RustdeskInstall`):
 *
 *   GET  /remote-connect/rustdesk-install/{deviceId}   starea, pentru progres
 *   POST /remote-connect/rustdesk-install/{deviceId}   marcheaza aparatul + FRC
 */
export type RustdeskInstallStatus = RustdeskDeviceState & {
  ready: boolean
  accessDisabled: boolean
  installMode: 'start' | 'demand'
  /** Agentul a raportat un ID RustDesk. */
  installed: boolean
  /** Cineva a cerut instalarea din portal. */
  requested: boolean
}

export function rustdeskInstallQuery(deviceId: number, poll = false) {
  return queryOptions({
    queryKey: ['remote-connect', 'rustdesk-install', deviceId],
    queryFn: () => apiGet<RustdeskInstallStatus>(`/remote-connect/rustdesk-install/${deviceId}`),
    refetchInterval: poll ? 3000 : false,
  })
}

export function requestRustdeskInstall(deviceId: number) {
  return apiPost<RustdeskInstallStatus & { requested: boolean }>(`/remote-connect/rustdesk-install/${deviceId}`, {})
}

/** Textul starii, acelasi peste tot. */
export function installStateLabel(state: string): string {
  switch (state) {
    case 'downloading':
      return 'Downloading'
    case 'installing':
      return 'Installing'
    case 'installed':
      return 'Installed'
    case 'failed':
      return 'Failed'
    case 'not_installed':
      return 'Not installed'
    default:
      return 'Not installed'
  }
}
